import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowDownLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { inboxApi, contactsApi } from "@/lib/api";
import { initials, avatarColor, cn } from "@/lib/utils";
import type { InboxMessage } from "@/types/api";
import Composer from "./composer";

interface Props {
  conversationId: string;
}

export default function ThreadDetail({ conversationId }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["inbox", "thread", conversationId],
    queryFn: () => inboxApi.threadMessages(conversationId),
  });

  if (isLoading) {
    return (
      <div className="flex h-full flex-col gap-3 p-6">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-ink-muted">
        Couldn't load this thread. Try refreshing.
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <h2 className="font-display text-lg font-semibold leading-tight text-ink">
          {data.subject ?? "(no subject)"}
        </h2>
        <p className="mt-0.5 text-xs text-ink-dim">
          {data.messages.length} message{data.messages.length === 1 ? "" : "s"}
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-surface px-6 py-5 scrollbar-thin">
        <div className="mx-auto max-w-3xl space-y-3">
          {data.messages.map((msg) => (
            <MessageCard key={msg.graphMessageId} msg={msg} />
          ))}
        </div>
      </div>

      {/* Composer */}
      <Composer
        conversationId={conversationId}
        latestMessage={data.messages.length > 0 ? data.messages[data.messages.length - 1]! : null}
      />
    </div>
  );
}

function MessageCard({ msg }: { msg: InboxMessage }) {
  const inbound = msg.direction === "inbound";
  return (
    <article
      className={cn(
        "rounded-lg border bg-surface-panel shadow-sm transition-shadow",
        inbound ? "border-line" : "border-line",
      )}
    >
      <header className="flex items-start gap-3 px-4 pt-4">
        <Avatar className="h-9 w-9">
          <AvatarFallback className={avatarColor(msg.fromAddress ?? msg.fromName)}>
            {initials(msg.fromName ?? msg.fromAddress)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <SenderLink
              name={msg.fromName ?? msg.fromAddress ?? "Unknown"}
              email={msg.fromAddress}
            />
            {msg.fromName && msg.fromAddress && (
              <span className="text-[12px] text-ink-dim">&lt;{msg.fromAddress}&gt;</span>
            )}
            {inbound ? (
              <Badge variant="accent" className="gap-1">
                <ArrowDownLeft className="h-2.5 w-2.5" /> inbound
              </Badge>
            ) : (
              <Badge variant="success" className="gap-1">
                <ArrowUpRight className="h-2.5 w-2.5" /> sent
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-[12px] text-ink-muted">
            to {msg.toAddresses.join(", ") || "—"}
            {msg.ccAddresses.length > 0 && <> · cc {msg.ccAddresses.join(", ")}</>}
          </p>
        </div>
        <time className="shrink-0 text-[12px] text-ink-dim">
          {format(new Date(msg.receivedAt), "MMM d, h:mm a")}
        </time>
      </header>

      <div className="px-4 pb-4 pt-3">
        <MessageBody
          graphMessageId={msg.graphMessageId}
          snippet={msg.snippet}
          inlineBodyText={(msg as InboxMessage & { body_text?: string | null }).body_text ?? null}
          inlineBodyHtml={(msg as InboxMessage & { body_html?: string | null }).body_html ?? null}
        />
      </div>
    </article>
  );
}

function MessageBody({
  graphMessageId,
  snippet,
  inlineBodyText,
  inlineBodyHtml,
}: {
  graphMessageId: string;
  snippet: string | null;
  inlineBodyText: string | null;
  inlineBodyHtml: string | null;
}) {
  // Mock + manually-created messages ship body inline (no Graph fetch
  // needed). Render those directly without firing the lazy body query —
  // saves a round-trip and works for non-Graph data.
  const hasInline = !!(inlineBodyText || inlineBodyHtml);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["inbox", "body", graphMessageId],
    queryFn: () => inboxApi.messageBody(graphMessageId),
    staleTime: 30 * 60_000, // KV caches for 30 min
    enabled: !hasInline && !!graphMessageId,
  });

  if (hasInline) {
    if (inlineBodyHtml) {
      return (
        <div
          className="prose prose-sm max-w-none text-sm text-ink [&_a]:text-brand-deep"
          dangerouslySetInnerHTML={{ __html: inlineBodyHtml }}
        />
      );
    }
    return (
      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink">
        {inlineBodyText}
      </pre>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-11/12" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-ink-muted">
        {snippet ?? "Couldn't load message body."}
      </p>
    );
  }

  if (data.content_type === "html" || data.content_type === "text/html") {
    return (
      <div
        className="prose prose-sm max-w-none text-sm text-ink [&_a]:text-brand-deep"
        dangerouslySetInnerHTML={{ __html: data.content }}
      />
    );
  }

  return (
    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink">
      {data.content}
    </pre>
  );
}

// Sender name in the inbox message header. On click, looks up the matching
// CRM contact by email and navigates to their detail page. If no contact
// exists with that email, falls through to the contacts list filtered by
// the email so the user can see "no match" + decide what to do (often:
// add them as a new contact). Spinner during the resolve.
function SenderLink({ name, email }: { name: string; email: string | null }) {
  const navigate = useNavigate();
  const [resolving, setResolving] = useState(false);

  const onClick = async () => {
    if (!email || resolving) return;
    setResolving(true);
    try {
      const r = await contactsApi.list({ q: email, limit: 1 });
      const hit = r.contacts.find((c) => (c.email ?? "").toLowerCase() === email.toLowerCase()) ?? r.contacts[0];
      if (hit?.id) {
        navigate(`/contacts/${hit.id}`);
      } else {
        // No contact yet for this sender — drop them on the filtered list
        // so they can quickly add one.
        navigate(`/contacts?q=${encodeURIComponent(email)}`);
      }
    } catch {
      navigate(`/contacts?q=${encodeURIComponent(email)}`);
    } finally {
      setResolving(false);
    }
  };

  if (!email) {
    return <span className="text-sm font-semibold text-ink">{name}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={resolving}
      className="inline-flex items-center gap-1 rounded text-sm font-semibold text-ink transition-colors hover:text-brand-deep hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-deep/40 disabled:opacity-60"
      title={`Open contact for ${email}`}
    >
      {name}
      {resolving && <Loader2 className="h-3 w-3 animate-spin" />}
    </button>
  );
}
